import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage(
          language === 'nl'
            ? 'Geen verificatie token gevonden.'
            : 'No verification token found.'
        );
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-email/${token}`);
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email);
        
        // Refresh user data to update email_verified status
        if (refreshUser) {
          await refreshUser();
        }
      } catch (err) {
        setStatus('error');
        setMessage(
          err.response?.data?.detail ||
          (language === 'nl'
            ? 'Verificatie mislukt. De link is mogelijk verlopen.'
            : 'Verification failed. The link may have expired.')
        );
      }
    };

    verifyEmail();
  }, [token, language, refreshUser]);

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-accent animate-spin" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'E-mail wordt geverifieerd...' : 'Verifying email...'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {language === 'nl'
                ? 'Even geduld, we controleren je verificatie link.'
                : 'Please wait while we verify your email.'}
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-10 h-10 text-success" />
            </motion.div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'E-mail geverifieerd!' : 'Email verified!'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-2">
              {message}
            </p>
            {email && (
              <p className="text-sm text-slate-400 dark:text-slate-500 mb-6">
                {email}
              </p>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
              data-testid="verify-success-dashboard-btn"
            >
              {language === 'nl' ? 'Naar dashboard' : 'Go to dashboard'}
            </button>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-error" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'Verificatie mislukt' : 'Verification failed'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {message}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
              >
                {language === 'nl' ? 'Naar inloggen' : 'Go to login'}
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-lg">
          {renderContent()}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
